import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SearchKeyword from '../common/SearchKeyword';
import thumb_img from '../../../assets/images/board_thumb_default.png';
import Pagination from '../common/Pagination';
import DetailPopup from '../common/DetailPopup';

export default function Content({ activeCategory }) {
  const [searchParams, setSearchParams] = useState({
    filter: 'title',
    keyword: '',
  });

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // 팝업 상태
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // 카테고리 매핑 - 인터뷰는 하위 카테고리가 하나뿐이므로 2,0으로 조회
  const categoryMapping = {
    인터뷰: { category1: 2, category2: 0 }  // 하위 카테고리 구분 없이 전체 조회
  };

  const customFilterOptions = [
    { value: 'title', label: '제목' },
    { value: 'content', label: '내용' },
    { value: 'all', label: '제목+내용' },
  ];

  // 실제 데이터 가져오기 함수
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const categoryConfig = categoryMapping[activeCategory] || {
        category1: 2,
        category2: 0,
      };

      console.log('=== 사용자 페이지 API 호출 ===');
      console.log('활성 카테고리:', activeCategory);
      console.log('매핑된 카테고리:', categoryConfig);

      // API 파라미터 설정
      const apiParams = {
        curPage: currentPage,
      };

      // 검색 파라미터가 있을 때만 추가
      if (searchParams?.keyword && searchParams.keyword.trim()) {
        apiParams.keyword = searchParams.keyword.trim();
        apiParams.filter = searchParams.filter || 'title';
        console.log('검색 조건 추가:', apiParams);
      }

      const endpoint = `/api/review/categories/${categoryConfig.category1}/${categoryConfig.category2}`;

      console.log(`API 호출: ${endpoint}`, apiParams);

      const response = await axios.get(endpoint, {
        params: apiParams,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('API 응답:', response.data);

      if (response.data && response.data.success !== false) {
        const reviewList = response.data.list || [];
        const paginationData = response.data.pagination || {};

        console.log('조회된 리뷰 목록:', reviewList);

        setReviews(reviewList);

        // 페이지네이션 설정
        const totalPages = paginationData.totalPages || 1;
        setPagination({
          ...paginationData,
          totalPages: totalPages,
        });
      } else {
        console.error('API 오류:', response.data);
        setError('데이터를 불러올 수 없습니다');
        setReviews([]);
      }
    } catch (error) {
      console.error('=== API 호출 실패 ===', error);

      let errorMessage = '데이터를 불러오는 중 오류가 발생했습니다';
      if (error.response?.status === 404) {
        errorMessage = '해당 카테고리의 설치사례가 없습니다';
        setReviews([]); // 404는 에러가 아닌 빈 결과로 처리
        setError(null);
      } else {
        setError(errorMessage);
        setReviews([]);
      }
    } finally {
      setLoading(false);
    }
  }, [activeCategory, currentPage, searchParams]);

  // 카테고리 변경 시 데이터 다시 로드
  useEffect(() => {
    setCurrentPage(1); // 카테고리 변경 시 첫 페이지로
    fetchReviews();
  }, [activeCategory]);

  // 페이지 변경 시 데이터 로드
  useEffect(() => {
    fetchReviews();
  }, [currentPage]);

  // 검색 파라미터 변경 시 데이터 로드
  useEffect(() => {
    setCurrentPage(1); // 검색 시 첫 페이지로
    fetchReviews();
  }, [searchParams]);

  const handleSearch = (searchData) => {
    console.log('검색 실행:', searchData);
    setSearchParams(searchData);

    // URL 파라미터 업데이트 (선택사항)
    const params = new URLSearchParams();
    if (searchData.keyword) {
      params.set('keyword', searchData.keyword);
      params.set('filter', searchData.filter);
    }
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${params}`
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    console.log('페이지 변경:', page);
  };
  
  const handleEventClick = (reviewData) => {
    console.log('설치사례 전체 데이터:', reviewData); // 디버깅용

    const detailData = {
      title: reviewData.r_title,
      subtitle: activeCategory || '설치사례',
      videoUrl: reviewData.r_url || '', // r_url 사용
      content: reviewData.r_content || '설치사례 내용입니다.',
      imageUrl: reviewData.r_file1_s || '', // r_file1_s 사용
      category1: reviewData.r_category1,
      category2: reviewData.r_category2,
      addDate: reviewData.r_add_dt,
    };

    console.log('변환된 상세 데이터:', detailData); // 디버깅용
    setSelectedEvent(detailData);
    setIsDetailOpen(true);
  };

  return (
    <>
      <div className="s3_1_content">
        <SearchKeyword
          onSearch={handleSearch}
          placeholder="제목으로 검색하세요"
          filterOptions={customFilterOptions}
          initialFilter="title"
        />

        <div className="center txt-left">
          {/* 로딩 상태 */}
          {loading && (
            <div
              className="loading-state"
              style={{ textAlign: 'center', padding: '50px' }}
            >
              <p>설치사례를 불러오는 중...</p>
            </div>
          )}

          {/* 에러 상태 */}
          {error && (
            <div
              className="error-state"
              style={{ textAlign: 'center', padding: '50px', color: 'red' }}
            >
              <p>{error}</p>
            </div>
          )}

          {/* 데이터 표시 */}
          {!loading && !error && (
            <div className="mt-4">
              <div className="board_wrap mb-5">
                {reviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>해당 카테고리의 설치사례가 없습니다.</p>
                  </div>
                ) : (
                  <div className="row-flex-center gap-30 flex-wrap">
                    {reviews.map((review) => (
                      <div
                        key={review.r_no}
                        className="board_item pointer"
                        onClick={() => {
                          console.log('설치사례 클릭:', review);
                          handleEventClick(review);
                        }}
                      >
                        <div className="board_img w-100">
                          {/* 첨부 이미지가 있으면 표시, 없으면 기본 이미지 */}
                          <img
                            src={
                              review.r_file1_s
                                ? `/resources/upload/${review.r_file1_s}`
                                : thumb_img
                            }
                            alt={review.r_title}
                            onError={(e) => {
                              console.log(
                                '이미지 로드 실패:',
                                review.r_file1_s
                              ); // 디버깅용
                              e.target.src = thumb_img; // 이미지 로드 실패 시 기본 이미지
                            }}
                          />
                        </div>
                        <p className="prt-semi-bold txt-left fs-20 fc-black1 mt-2 mb-5 txt-ellipsis">
                          {review.r_title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 페이지네이션 */}
              {pagination.totalPages > 1 && (
                <div className="pagination row-flex-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail 팝업 */}
      <DetailPopup
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        data={selectedEvent}
      />
    </>
  );
}